import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type DiffStatus,
  computeDiffStatus,
} from '~/components/common/enrichment-diff-table/diff-status.util';
import { EnrichmentOptContent } from '~/components/common/enrichment-opt-content';
import type { OptimizationContentType, ProductOptimization } from '~/types/enrichment';
import { cn } from '~/utils/cn';
import { formatProductHighlightsForFeed } from '~/utils/product-highlights';

import {
  formatProductDetailAttrsForFeed,
  parseEnrichedProductDetailAttrs,
} from '../../utils/parse-enriched-product-details';

/** One row, one attribute — a 180px name column plus three equal value
 *  columns, instead of the Compare Delivery page's "label above, 3 columns
 *  below" layout. That page's own product decision was to keep the rail-with-
 *  three-panes shape (`docs/delivery-compare-view.md`); this is a different
 *  page with a different, spec-driven layout, not a change to that one. */
const ROW_GRID = 'grid grid-cols-1 gap-x-6 gap-y-1 md:grid-cols-[180px_1fr_1fr_1fr]';

const DIFF_DOT_CLASS: Record<DiffStatus, string> = {
  enriched: 'bg-success',
  modified: 'bg-warning',
  removed: 'bg-error',
  unchanged: '',
};

/**
 * One-time column header for the comparison table — Attribute / Pre-
 * enrichment / Enriched (carrying the Changed/Added legend inline, since nothing
 * elsewhere explained those dots) / Proposed delivery.
 */
export function DeliveryWizardComparisonHeader() {
  const { t } = useTranslation('common');
  return (
    <div
      className={cn(
        ROW_GRID,
        'bg-muted/40 hidden items-center rounded-t-lg px-4 py-2.5 text-xs font-medium md:grid',
      )}
      data-testid="delivery-wizard-preview-comparison-header"
    >
      <span className="text-muted-foreground">{t('deliveryWizard.preview.table.attribute')}</span>
      <span className="text-muted-foreground">
        {t('deliveryWizard.preview.table.preEnrichment')}
      </span>
      <span className="text-muted-foreground flex flex-wrap items-center gap-3">
        {t('deliveryWizard.preview.table.enriched')}
        <span className="inline-flex items-center gap-1 font-normal">
          <span className="bg-warning size-2 shrink-0 rounded-full" aria-hidden="true" />
          {t('deliveryWizard.preview.table.legendChanged')}
        </span>
        <span className="inline-flex items-center gap-1 font-normal">
          <span className="bg-success size-2 shrink-0 rounded-full" aria-hidden="true" />
          {t('deliveryWizard.preview.table.legendAdded')}
        </span>
      </span>
      <span className="text-muted-foreground">{t('deliveryWizard.preview.table.proposed')}</span>
    </div>
  );
}

interface RowProps {
  opt: ProductOptimization;
  /** Overrides the enriched/proposed value shown — used for the Search
   *  Descriptors view of the "description" field, which merges in phrases. */
  overrideValue?: string;
  /** Shown under an empty Enriched cell to explain why — used when Phrase
   *  Descriptions' Level 3 failed and there is no rewrite to deliver. */
  enrichedError?: string;
  /** Trailing part of `overrideValue` to render bold in the Proposed column —
   *  the Search Descriptors phrases appended to the description. */
  highlightedSuffix?: string;
}

/**
 * Parses the `{ content, digital_source_type }` envelope Structured Title/
 * Description arrive in (the same shape `content-scoring.types.ts`'s
 * `StructuredContentValue` names elsewhere — not imported here, since this
 * wizard's own `ProductOptimization.value` is an opaque string and pulling in
 * a content-enrichment type would point a dependency the wrong way) and
 * formats it the way the GMC feed export encodes it (the "Typed text" rule):
 * `type:"content"`. Falls back to the raw value unchanged if the shape
 * doesn't match, rather than blanking the cell.
 */
function formatStructuredContentForFeed(value: string): string {
  try {
    const parsed = JSON.parse(value) as { content?: unknown; digital_source_type?: unknown };
    if (typeof parsed.content === 'string' && typeof parsed.digital_source_type === 'string') {
      return `${parsed.digital_source_type}:"${parsed.content}"`;
    }
  } catch {
    // Not JSON, or not the expected shape — fall through to the raw value.
  }
  return value;
}

/**
 * Whether this content type should render as the plain GMC-feed-encoded
 * string rather than through its own type-specific renderer — "as-is",
 * matching the Catalog Operations export's GMC Feed Transformation rules
 * instead of this app's own bullet/key-value prettifying.
 */
function formatForFeedDisplay(value: string, contentType: OptimizationContentType): string {
  if (contentType === 'bullets') return formatProductHighlightsForFeed(value);
  if (contentType === 'structured-json') return formatStructuredContentForFeed(value);
  return value;
}

/**
 * One row of the pre-delivery comparison table. Every row shown here already
 * passed the "will this be delivered" filter one level up (the step only
 * renders the attributes the operator selected in Step 1), so the Proposed
 * column is just the value — no per-row "delivered" marker is needed.
 */
export function DeliveryWizardComparisonRow({
  opt,
  overrideValue,
  enrichedError,
  highlightedSuffix,
}: RowProps) {
  const { t: tCatalog } = useTranslation('catalog-operations');
  const { t } = useTranslation('common');
  const enrichedValue = overrideValue ?? opt.value;
  const rowTestId = `delivery-wizard-preview-comparison-row-${opt.field}`;

  if (opt.contentType === 'product-details') {
    const currentAttrs = opt.currentAttributes ?? [];
    const enrichedAttrs = parseEnrichedProductDetailAttrs(opt.value);
    const proposedAttrs =
      overrideValue !== undefined ? parseEnrichedProductDetailAttrs(overrideValue) : enrichedAttrs;
    const diffStatus = computeDiffStatus(currentAttrs, enrichedAttrs);
    return (
      <div
        className={cn(ROW_GRID, 'items-start border-t px-4 py-3.5 text-[15px]')}
        data-testid={rowTestId}
      >
        <RowLabel label={opt.label} />
        <Cell
          caption={t('deliveryWizard.preview.table.preEnrichment')}
          testId={`${rowTestId}-pre-enrichment`}
        >
          {currentAttrs.length > 0 ? (
            <EnrichmentOptContent
              value={formatProductDetailAttrsForFeed(currentAttrs)}
              type="plain"
              testId={`${rowTestId}-pre-enrichment-content`}
            />
          ) : (
            <EmptyValue />
          )}
        </Cell>
        <Cell caption={t('deliveryWizard.preview.table.enriched')} testId={`${rowTestId}-enriched`}>
          {enrichedAttrs.length > 0 ? (
            <div className="flex items-start gap-2">
              {diffStatus !== 'unchanged' && (
                <span
                  className={cn('mt-1.5 size-2 shrink-0 rounded-full', DIFF_DOT_CLASS[diffStatus])}
                  title={tCatalog(`deliveryCompare.attributeTable.changedHint.${diffStatus}`)}
                />
              )}
              <div className="min-w-0 flex-1 break-words">
                <EnrichmentOptContent
                  value={formatProductDetailAttrsForFeed(enrichedAttrs)}
                  type="plain"
                  testId={`${rowTestId}-enriched-content`}
                />
              </div>
            </div>
          ) : (
            <EmptyValue />
          )}
        </Cell>
        <Cell caption={t('deliveryWizard.preview.table.proposed')} testId={`${rowTestId}-proposed`}>
          <EnrichmentOptContent
            value={formatProductDetailAttrsForFeed(proposedAttrs)}
            type="plain"
            testId={`${rowTestId}-proposed-content`}
          />
        </Cell>
      </div>
    );
  }

  // Product Highlight / Structured Title / Structured Description render as
  // the plain GMC-feed-encoded string ("as-is") instead of this app's own
  // bullet/key-value prettifying — see `formatForFeedDisplay`. Diff status
  // still compares the raw values: the encoding is a deterministic 1:1
  // transform, so raw-value equality and encoded-value equality agree.
  //
  // `overrideValue` is only ever set for the Search Descriptors merge onto
  // Description (see `delivery-wizard-preview-step.tsx`), which appends the
  // matched phrases with a `|` separator — `opt.contentType` for description
  // falls through to 'text', whose default rendering treats any `|` as a
  // bullet-list delimiter. The export preview shows the merge as one
  // continuous line with the phrases inline, not a bullet, so this forces
  // plain rendering whenever that merge is present.
  const displayContentType: OptimizationContentType =
    opt.contentType === 'bullets' ||
    opt.contentType === 'structured-json' ||
    overrideValue !== undefined
      ? 'plain'
      : opt.contentType;
  const currentDisplay = opt.currentValue
    ? formatForFeedDisplay(opt.currentValue, opt.contentType)
    : opt.currentValue;
  const enrichedDisplay = opt.value ? formatForFeedDisplay(opt.value, opt.contentType) : opt.value;
  const proposedDisplay = enrichedValue
    ? formatForFeedDisplay(enrichedValue, opt.contentType)
    : enrichedValue;

  const diffStatus = computeDiffStatus(opt.currentValue, opt.value);
  return (
    <div
      className={cn(ROW_GRID, 'items-start border-t px-4 py-3.5 text-[15px]')}
      data-testid={rowTestId}
    >
      <RowLabel label={opt.label} />
      <Cell
        caption={t('deliveryWizard.preview.table.preEnrichment')}
        testId={`${rowTestId}-pre-enrichment`}
      >
        {currentDisplay ? (
          <EnrichmentOptContent
            value={currentDisplay}
            type={displayContentType}
            testId={`${rowTestId}-pre-enrichment-content`}
          />
        ) : (
          <EmptyValue />
        )}
      </Cell>
      <Cell caption={t('deliveryWizard.preview.table.enriched')} testId={`${rowTestId}-enriched`}>
        {enrichedDisplay ? (
          <div className="flex items-start gap-2">
            {diffStatus !== 'unchanged' && (
              <span
                className={cn('mt-1.5 size-2 shrink-0 rounded-full', DIFF_DOT_CLASS[diffStatus])}
                title={tCatalog(`deliveryCompare.attributeTable.changedHint.${diffStatus}`)}
              />
            )}
            <div className="min-w-0 flex-1 break-words">
              <EnrichmentOptContent
                value={enrichedDisplay}
                type={displayContentType}
                testId={`${rowTestId}-enriched-content`}
              />
            </div>
          </div>
        ) : (
          <>
            <EmptyValue />
            {enrichedError && (
              <p
                className="text-muted-foreground mt-1 text-xs italic"
                data-testid={`${rowTestId}-enriched-error`}
              >
                {t('deliveryWizard.preview.table.level3Failed', { error: enrichedError })}
              </p>
            )}
          </>
        )}
      </Cell>
      <Cell caption={t('deliveryWizard.preview.table.proposed')} testId={`${rowTestId}-proposed`}>
        {/* Empty only for a Phrase Descriptions row with no Level 3. */}
        {proposedDisplay ? (
          highlightedSuffix && proposedDisplay.endsWith(highlightedSuffix) ? (
            // Same markup as EnrichmentOptContent's 'plain' branch, split so the
            // appended Search Descriptors phrases stand out from the description.
            <p className="text-sm leading-relaxed">
              {proposedDisplay.slice(0, -highlightedSuffix.length)}
              <strong className="font-semibold">{highlightedSuffix}</strong>
            </p>
          ) : (
            <EnrichmentOptContent
              value={proposedDisplay}
              type={displayContentType}
              testId={`${rowTestId}-proposed-content`}
            />
          )
        ) : (
          <EmptyValue />
        )}
      </Cell>
    </div>
  );
}

function RowLabel({ label }: { label: string }) {
  return <p className="text-foreground mb-2 text-sm font-semibold md:mb-0">{label}</p>;
}

/** Repeats the column name inline, but only below `md` where the columns
 *  stack and the shared header above is no longer beside the value. */
function Cell({
  caption,
  children,
  testId,
}: {
  caption: string;
  children: ReactNode;
  /** Automation hook for the value cell. */
  testId?: string;
}) {
  return (
    <div className="min-w-0 break-words" data-testid={testId}>
      <p className="text-muted-foreground mb-0.5 text-xs md:hidden">{caption}</p>
      {children}
    </div>
  );
}

function EmptyValue() {
  return <p className="text-muted-foreground text-sm">—</p>;
}
